import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EMPEmptyState from "../../../shared/components/EMPEmptyState/EMPEmptyState";
import { formatPriceValue } from "../../../utils/FormatPrice";

const SECTIONS = [
  { key: "ELEMENT", label: "elements" },
  { key: "CHARGING", label: "charging" },
  { key: "UPGRADES", label: "upgrades" },
  { key: "GEAR", label: "gear" },
];

const priceType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
const sectionType = PropTypes.shape({
  pricingDetail: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      msrp: priceType,
    })
  ),
});

export const preOrderPricingSummaryType = PropTypes.shape({
  baseMsrp: priceType,
  sections: PropTypes.shape({
    ELEMENT: sectionType,
    CHARGING: sectionType,
    UPGRADES: sectionType,
    GEAR: sectionType,
  }),
  finalMsrp: priceType,
  estimatedTaxesAndFees: priceType,
  totalEstimatedPrice: priceType,
});

const PriceRow = ({ label, value, total }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      columnGap: 4,
      py: 1,
      pl: total ? 0 : 6,
      mt: total ? 2 : 0,
    }}
  >
    <Typography
      component="dt"
      variant={total ? "subtitle1" : "body1"}
      sx={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      {label}
    </Typography>
    <Typography
      component="dd"
      variant={total ? "subtitle1" : "body1"}
      sx={{ m: 0, textAlign: "right", color: "#102B4E" }}
    >
      {value === null || value === undefined ? (
        <EMPEmptyState />
      ) : (
        formatPriceValue(value)
      )}
    </Typography>
  </Box>
);

PriceRow.propTypes = {
  label: PropTypes.string,
  value: priceType,
  total: PropTypes.bool,
};

PriceRow.defaultProps = {
  label: "",
  value: null,
  total: false,
};

const PreOrderPricingSummary = ({ pricingSummary }) => {
  const { t } = useTranslation("emp");

  return (
    <Box component="section" data-testid="preorder-pricing-summary">
      <Typography
        variant="h3"
        sx={(theme) => ({
          mb: 5,
          fontFamily: theme.typography.subtitle1.fontFamily,
          fontSize: "24px",
          fontWeight: 700,
          lineHeight: "32px",
        })}
      >
        {t("PreOrderPricingSummary.title")}
      </Typography>
      <Box
        component="dl"
        data-testid="preorder-pricing-values"
        sx={{ m: 0, width: { xs: "100%", md: "50%" } }}
      >
        <PriceRow
          label={t("OrderDetailsPricingSummary.baseMSRP")}
          value={pricingSummary?.baseMsrp}
        />
        {SECTIONS.map(({ key, label }) => {
          const details = pricingSummary?.sections?.[key]?.pricingDetail;

          return (
            <Box key={key} sx={{ mt: 2 }}>
              <Typography
                component="dt"
                variant="subtitle1"
                sx={{ mb: 1, pl: 6 }}
              >
                {t(`PreOrderPricingSummary.${label}`)}
              </Typography>
              <Box component="dd" sx={{ m: 0 }}>
                <Box component="dl" sx={{ m: 0 }}>
                  {Array.isArray(details) &&
                    details
                      .filter(Boolean)
                      .map((item, index) => (
                        <PriceRow
                          key={`${key}-${index}`}
                          label={item.description}
                          value={item.msrp}
                        />
                      ))}
                </Box>
              </Box>
            </Box>
          );
        })}
        <PriceRow
          label={t("PreOrderPricingSummary.finalMsrp")}
          value={pricingSummary?.finalMsrp}
          total
        />
        <PriceRow
          label={t("PreOrderPricingSummary.estimatedTaxesAndFees")}
          value={pricingSummary?.estimatedTaxesAndFees}
        />
        <PriceRow
          label={t("PreOrderPricingSummary.totalEstimatedPrice")}
          value={pricingSummary?.totalEstimatedPrice}
          total
        />
      </Box>
    </Box>
  );
};

PreOrderPricingSummary.propTypes = {
  pricingSummary: preOrderPricingSummaryType,
};

PreOrderPricingSummary.defaultProps = {
  pricingSummary: null,
};

export default PreOrderPricingSummary;
